import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatWeekdayNames } from '@physioedge/shared';
import { api, ApiError } from '../api';
import { Button, Input, StatusBadge } from './ui';
import { Time12hInput } from './Time12hInput';
import { formatTime12h } from '../lib/time12h';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export type AdminScheduleSummary = {
  id: string;
  clinicLocationId: string;
  locationName?: string | null;
  locationCity?: string | null;
  title?: string | null;
  startDate: string;
  endDate: string;
  daysOfWeek: number[];
  dailyStartTime: string;
  dailyEndTime: string;
  capacityPerSession: number;
  sessionCount: number;
  isPublished: boolean;
};

type ScheduleEvent = {
  id: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  capacity: number;
  bookedCount: number;
};

function hoursFromDailyWindow(startTime: string, endTime: string): number | null {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  if ([sh, sm, eh, em].some(n => Number.isNaN(n))) return null;
  const mins = eh! * 60 + em! - (sh! * 60 + sm!);
  if (mins <= 0) return null;
  return Math.max(1, Math.round(mins / 60));
}

function formatEventDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const dt = new Date(Date.UTC(year!, month! - 1, day));
  return dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function scheduleNameOf(s: AdminScheduleSummary): string {
  return s.title || formatWeekdayNames(s.daysOfWeek ?? []) || 'Internship schedule';
}

export function AdminScheduleCard({
  clinicId,
  schedule,
  branchLabel,
  onRequestDelete,
  onChanged,
}: {
  clinicId: string;
  schedule: AdminScheduleSummary;
  branchLabel: string;
  onRequestDelete: () => void;
  onChanged: (message: string) => void;
}) {
  const qc = useQueryClient();
  const sessionsKey = ['admin-clinic-schedule-sessions', clinicId, schedule.id];
  const [panel, setPanel] = useState<'idle' | 'edit' | 'events'>('idle');
  const [editForm, setEditForm] = useState({
    title: schedule.title ?? '',
    startDate: schedule.startDate,
    endDate: schedule.endDate,
    daysOfWeek: schedule.daysOfWeek ?? [],
    dailyStartTime: schedule.dailyStartTime,
    dailyEndTime: schedule.dailyEndTime,
    capacityPerSession: String(schedule.capacityPerSession),
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkForm, setBulkForm] = useState({ startTime: '', endTime: '', capacity: '' });
  const [savingBulk, setSavingBulk] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [eventForm, setEventForm] = useState({ startTime: '', endTime: '', capacity: '' });
  const [savingEvent, setSavingEvent] = useState(false);
  const [pendingBulk, setPendingBulk] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [visibilityError, setVisibilityError] = useState('');

  const sessionsQ = useQuery({
    queryKey: sessionsKey,
    queryFn: () => api.get<{ sessions: ScheduleEvent[] }>(
      `/api/admin/clinics/${clinicId}/schedules/${schedule.id}/sessions`,
    ),
    enabled: panel === 'events',
  });

  const sessions = sessionsQ.data?.sessions ?? [];
  const scheduleName = scheduleNameOf(schedule);

  useEffect(() => {
    if (!pendingBulk || panel !== 'events' || sessions.length === 0) return;
    setSelectedIds(sessions.map(ev => ev.id));
    setShowBulk(true);
    setPendingBulk(false);
  }, [pendingBulk, panel, sessions]);

  const openEdit = () => {
    setEditError('');
    setEditForm({
      title: schedule.title ?? '',
      startDate: schedule.startDate,
      endDate: schedule.endDate,
      daysOfWeek: [...(schedule.daysOfWeek ?? [])],
      dailyStartTime: schedule.dailyStartTime,
      dailyEndTime: schedule.dailyEndTime,
      capacityPerSession: String(schedule.capacityPerSession),
    });
    setPanel('edit');
  };

  const openEvents = (opts?: { bulk?: boolean }) => {
    setEventsError('');
    setSelectedIds([]);
    setShowBulk(false);
    setEditingEventId(null);
    setPendingBulk(opts?.bulk === true);
    setPanel('events');
  };

  const saveSchedule = async () => {
    setSavingEdit(true);
    setEditError('');
    try {
      await api.patch(`/api/admin/clinics/${clinicId}/schedules/${schedule.id}`, {
        title: editForm.title || undefined,
        startDate: editForm.startDate,
        endDate: editForm.endDate,
        daysOfWeek: editForm.daysOfWeek,
        dailyStartTime: editForm.dailyStartTime,
        dailyEndTime: editForm.dailyEndTime,
        capacityPerSession: Number(editForm.capacityPerSession),
      });
      await qc.invalidateQueries({ queryKey: ['admin-clinic-schedules', clinicId] });
      await qc.invalidateQueries({ queryKey: sessionsKey });
      setPanel('idle');
      onChanged('Schedule updated.');
    } catch (err) {
      setEditError(err instanceof ApiError ? err.friendlyMessage : 'Could not update schedule.');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const allSelected = sessions.length > 0 && selectedIds.length === sessions.length;

  const saveEvent = async (eventId: string) => {
    setSavingEvent(true);
    setEventsError('');
    try {
      const payload: { startTime?: string; endTime?: string; capacity?: number } = {
        startTime: eventForm.startTime,
        endTime: eventForm.endTime,
      };
      const cap = Number(eventForm.capacity);
      if (Number.isFinite(cap) && cap >= 1) payload.capacity = cap;
      await api.patch(`/api/admin/clinics/${clinicId}/schedules/${schedule.id}/sessions/${eventId}`, payload);
      setEditingEventId(null);
      await qc.invalidateQueries({ queryKey: sessionsKey });
      await qc.invalidateQueries({ queryKey: ['admin-clinic-schedules', clinicId] });
      onChanged('Event updated.');
    } catch (err) {
      setEventsError(err instanceof ApiError ? err.friendlyMessage : 'Could not update event.');
    } finally {
      setSavingEvent(false);
    }
  };

  const saveBulk = async () => {
    if (selectedIds.length === 0) return;
    const payload: { sessionIds: string[]; startTime?: string; endTime?: string; capacity?: number } = {
      sessionIds: selectedIds,
    };
    if (bulkForm.startTime) payload.startTime = bulkForm.startTime;
    if (bulkForm.endTime) payload.endTime = bulkForm.endTime;
    const cap = Number(bulkForm.capacity);
    if (bulkForm.capacity && Number.isFinite(cap) && cap >= 1) payload.capacity = cap;
    if (!payload.startTime && !payload.endTime && payload.capacity == null) {
      setEventsError('Set a start time, end time, or capacity.');
      return;
    }
    setSavingBulk(true);
    setEventsError('');
    try {
      await api.patch(`/api/admin/clinics/${clinicId}/schedules/${schedule.id}/sessions`, payload);
      await qc.invalidateQueries({ queryKey: sessionsKey });
      await qc.invalidateQueries({ queryKey: ['admin-clinic-schedules', clinicId] });
      setShowBulk(false);
      setBulkForm({ startTime: '', endTime: '', capacity: '' });
      onChanged(selectedIds.length === 1 ? 'Event updated.' : `${selectedIds.length} events updated.`);
    } catch (err) {
      setEventsError(err instanceof ApiError ? err.friendlyMessage : 'Could not update events.');
    } finally {
      setSavingBulk(false);
    }
  };

  const toggleVisibility = async () => {
    setSavingVisibility(true);
    setVisibilityError('');
    try {
      await api.patch(`/api/admin/clinics/${clinicId}/schedules/${schedule.id}/visibility`, {
        isPublished: !schedule.isPublished,
      });
      await qc.invalidateQueries({ queryKey: ['admin-clinic-schedules', clinicId] });
      onChanged(
        schedule.isPublished
          ? 'Hidden from students.'
          : 'Shown to students again.',
      );
    } catch (err) {
      setVisibilityError(err instanceof ApiError ? err.friendlyMessage : 'Could not update visibility.');
    } finally {
      setSavingVisibility(false);
    }
  };

  return (
    <div className="text-sm border border-line rounded-radius-sm px-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-medium text-ink">{scheduleName}</div>
            <StatusBadge status={schedule.isPublished ? 'published' : 'unpublished'} />
          </div>
          <div className="text-ink-soft mt-0.5">{branchLabel}</div>
          <div className="text-ink-soft">
            {formatWeekdayNames(schedule.daysOfWeek ?? []) || 'No days'}
            {' · '}
            {formatTime12h(schedule.dailyStartTime)}–{formatTime12h(schedule.dailyEndTime)}
          </div>
          <div className="text-xs text-ink-faint mt-0.5">
            {schedule.startDate} → {schedule.endDate}
            {' · '}
            {schedule.capacityPerSession} seats
          </div>
          {!schedule.isPublished && (
            <div className="text-xs text-ink-faint mt-1">Hidden from students</div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button size="sm" variant="ghost" onClick={toggleVisibility} loading={savingVisibility}>
            {schedule.isPublished ? 'Hide' : 'Show'}
          </Button>
          <Button size="sm" variant="ghost" onClick={openEdit}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => openEvents()}>Days</Button>
          <Button size="sm" variant="danger" onClick={onRequestDelete}>Delete</Button>
        </div>
      </div>
      {visibilityError && <p className="text-sm text-danger mt-2">{visibilityError}</p>}

      {panel === 'edit' && (
        <div className="space-y-3 pt-3 mt-2 border-t border-line">
          <p className="text-sm font-medium text-ink">Edit schedule</p>
          <p className="text-xs text-ink-soft">Updates days and times for this training.</p>
          <Input label="Title" placeholder="Leave blank to use the days, e.g. Sat · Mon · Wed" value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Start date" type="date" value={editForm.startDate} onChange={e => setEditForm(p => ({ ...p, startDate: e.target.value }))} />
            <Input label="End date" type="date" value={editForm.endDate} onChange={e => setEditForm(p => ({ ...p, endDate: e.target.value }))} />
            <Time12hInput label="Daily start" value={editForm.dailyStartTime} onChange={v => setEditForm(p => ({ ...p, dailyStartTime: v }))} allowAnyMinute />
            <Time12hInput label="Daily end" value={editForm.dailyEndTime} onChange={v => setEditForm(p => ({ ...p, dailyEndTime: v }))} allowAnyMinute />
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-ink-soft">Length</label>
              <div className="border border-line rounded-radius-sm px-3 py-3 text-sm bg-gray-50 text-ink min-h-[48px] flex items-center">
                {(() => {
                  const hours = hoursFromDailyWindow(editForm.dailyStartTime, editForm.dailyEndTime);
                  return hours == null
                    ? 'End must be after start'
                    : `${hours} hour${hours === 1 ? '' : 's'}`;
                })()}
              </div>
            </div>
            <Input label="Capacity" type="number" min={1} value={editForm.capacityPerSession} onChange={e => setEditForm(p => ({ ...p, capacityPerSession: e.target.value }))} />
          </div>
          <div>
            <div className="text-xs font-medium text-ink-soft mb-1.5">Days</div>
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS.map((label, day) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setEditForm(p => ({
                    ...p,
                    daysOfWeek: p.daysOfWeek.includes(day)
                      ? p.daysOfWeek.filter(d => d !== day)
                      : [...p.daysOfWeek, day].sort((a, b) => a - b),
                  }))}
                  className={`px-3 py-1.5 text-sm rounded-radius-sm border ${
                    editForm.daysOfWeek.includes(day) ? 'bg-teal text-white border-teal' : 'border-line text-ink-soft'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {editError && <p className="text-sm text-danger">{editError}</p>}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={saveSchedule}
              loading={savingEdit}
              disabled={!editForm.startDate || !editForm.endDate || editForm.daysOfWeek.length === 0}
            >
              Save schedule
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPanel('idle')}>Cancel</Button>
          </div>
        </div>
      )}

      {panel === 'events' && (
        <div className="space-y-3 pt-3 mt-2 border-t border-line">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-ink">Days</p>
              <p className="text-xs text-ink-soft">Change one day, or select a few and edit together.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {sessions.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-sand-strong hover:underline"
                  onClick={() => setSelectedIds(allSelected ? [] : sessions.map(ev => ev.id))}
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
              <Button
                size="sm"
                variant="secondary"
                disabled={selectedIds.length === 0}
                onClick={() => {
                  setEventsError('');
                  setShowBulk(true);
                }}
              >
                Bulk{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPanel('idle')}>Close</Button>
            </div>
          </div>

          {showBulk && (
            <div className="border border-line rounded-radius-sm p-3 space-y-3 bg-bg">
              <p className="text-xs font-medium text-ink">
                {selectedIds.length === 1 ? 'Edit 1 day' : `Edit ${selectedIds.length} days`}
              </p>
              <p className="text-xs text-ink-soft">Only fill what you want to change.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Time12hInput label="Start time" value={bulkForm.startTime} onChange={v => setBulkForm(p => ({ ...p, startTime: v }))} allowAnyMinute optional />
                <Time12hInput label="End time" value={bulkForm.endTime} onChange={v => setBulkForm(p => ({ ...p, endTime: v }))} allowAnyMinute optional />
                <Input label="Capacity" type="number" min={1} value={bulkForm.capacity} onChange={e => setBulkForm(p => ({ ...p, capacity: e.target.value }))} />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={saveBulk} loading={savingBulk}>Apply to selected</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowBulk(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {eventsError && <p className="text-sm text-danger">{eventsError}</p>}

          {sessionsQ.isLoading ? (
            <p className="text-sm text-ink-soft">Loading…</p>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-ink-soft">No days on this schedule.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto border border-line rounded-radius-sm divide-y divide-line">
              {sessions.map(ev => (
                <div key={ev.id} className="px-3 py-2 space-y-2">
                  <div className="flex items-start gap-2">
                    <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(ev.id)}
                        onChange={() => toggleSelected(ev.id)}
                        className="rounded border-line mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="font-medium text-ink">{formatEventDate(ev.sessionDate)}</span>
                        <span className="text-ink-soft">
                          {' · '}
                          {formatTime12h(ev.startTime)}–{formatTime12h(ev.endTime)}
                          {' · '}
                          {ev.capacity} seats
                          {ev.bookedCount > 0 ? ` · ${ev.bookedCount} booked` : ''}
                        </span>
                      </span>
                    </label>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEventsError('');
                        setEditingEventId(ev.id);
                        setEventForm({
                          startTime: ev.startTime,
                          endTime: ev.endTime,
                          capacity: String(ev.capacity),
                        });
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                  {editingEventId === ev.id && (
                    <div className="pl-6 space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Time12hInput label="Start time" value={eventForm.startTime} onChange={v => setEventForm(p => ({ ...p, startTime: v }))} allowAnyMinute />
                        <Time12hInput label="End time" value={eventForm.endTime} onChange={v => setEventForm(p => ({ ...p, endTime: v }))} allowAnyMinute />
                        <Input label="Capacity" type="number" min={1} value={eventForm.capacity} onChange={e => setEventForm(p => ({ ...p, capacity: e.target.value }))} />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => saveEvent(ev.id)} loading={savingEvent}>Save event</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingEventId(null)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
